import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { AgricolaBeneficiaryDialogComponent } from '../agricola-beneficiary-dialog/agricola-beneficiary-dialog.component';
import {
  AgricolaToastType,
  InsuranceBeneficiary,
  RelationshipCatalogStrategy,
  RelationshipOption
} from '../../../../../interfaces/agricola.interface';
import { AgricolaService } from '../../../../../services/agricola.service';

@Component({
  selector: 'app-agricola-beneficiaries-step',
  templateUrl: './agricola-beneficiaries-step.component.html',
  styleUrls: ['./agricola-beneficiaries-step.component.css'],
  standalone: false
})
export class AgricolaBeneficiariesStepComponent implements OnInit, OnDestroy {

  @Input() group!: FormGroup;
  @Input() selectedIndex: number = 0;
  @Input() steps: string[] = [];
  @Input() productCode = '';
  @Input() insuredIdentificationId = '';
  @Input() relationshipStrategy: RelationshipCatalogStrategy = 'global';
  @Output() nextStep = new EventEmitter<number>();
  @Output() previousStep = new EventEmitter<void>();

  showSuccessBanner = false;
  successMessage = 'Tu beneficiario se agregó correctamente. Verifica la información y continúa al siguiente paso.';
  showErrorBanner = false;
  errorMessage = '';
  toastMessage: string = '';
  toastType: AgricolaToastType = 'error';
  showToast: boolean = false;
  loadingRelationships = false;
  relationshipsLoadedKey = 0;
  relationshipsLoadFailed = false;
  relationshipOptions: RelationshipOption[] = [];
  private readonly relationshipOptionsSubject = new BehaviorSubject<RelationshipOption[]>([]);
  private readonly relationshipLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly relationshipLoadedKeySubject = new BehaviorSubject<number>(0);
  private readonly relationshipLoadFailedSubject = new BehaviorSubject<boolean>(false);
  private relationshipSubscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private agricolaService: AgricolaService
  ) {}

  ngOnInit(): void {
    this.loadRelationshipOptions();
  }

  ngOnDestroy(): void {
    this.relationshipSubscription.unsubscribe();
    this.relationshipOptionsSubject.complete();
    this.relationshipLoadingSubject.complete();
    this.relationshipLoadedKeySubject.complete();
    this.relationshipLoadFailedSubject.complete();
  }

  get beneficiaries(): FormArray {
    return this.group.get('beneficiaries') as FormArray;
  }

  get totalParticipation(): number {
    return this.group.get('totalParticipation')?.value || 0;
  }

  get availableParticipation(): number {
    return 100 - this.totalParticipation;
  }

  private updateTotalParticipation(): void {
    const total = this.beneficiaries.controls.reduce((acc, ctrl) => {
      const val = Number(ctrl.get('percentage')?.value) || 0;
      return acc + val;
    }, 0);
    this.group.get('totalParticipation')?.setValue(total);
  }

  retryLoadRelationships(): void { this.loadRelationshipOptions(); }

  private loadRelationshipOptions(): void {
    this.relationshipsLoadFailed = false;
    this.loadingRelationships = true;
    this.relationshipLoadingSubject.next(true);
    this.relationshipLoadFailedSubject.next(false);
    this.relationshipSubscription = this.agricolaService.getRelationshipOptions(this.relationshipStrategy, this.productCode)
      .pipe(finalize(() => {
        this.loadingRelationships = false;
        this.relationshipLoadingSubject.next(false);
      }))
      .subscribe({
        next: options => {
          this.relationshipOptions = options;
          this.relationshipOptionsSubject.next(options);
          this.relationshipsLoadedKey++;
          this.relationshipLoadedKeySubject.next(this.relationshipsLoadedKey);
        },
        error: () => {
          this.relationshipOptions = [];
          this.relationshipOptionsSubject.next([]);
          this.relationshipsLoadFailed = true;
          this.relationshipLoadFailedSubject.next(true);
          this.showToastMessage('No fue posible cargar el catálogo de parentescos.', 'error');
        }
      });
  }

  private openDialog(mode: 'add' | 'edit', index?: number): void {
    if (mode === 'add' && this.beneficiaries.length >= 5) {
      this.showToastMessage('Puedes agregar como máximo 5 beneficiarios.', 'warning');
      return;
    }

    if (!this.loadingRelationships && !this.relationshipOptions.length) {
      this.showToastMessage('El catálogo de parentescos no está disponible. Intenta nuevamente.', 'error');
      return;
    }

    let current: InsuranceBeneficiary | null = null;
    let available = this.availableParticipation;

    if (mode === 'edit' && index !== undefined) {
      const ctrl = this.beneficiaries.at(index) as FormGroup;
      current = ctrl.value as InsuranceBeneficiary;
      // al editar, se le devuelve al usuario su propio porcentaje
      available += current.percentage;
    }

    const existingIdentificationIds = this.beneficiaries.controls
      .map((ctrl, currentIndex) => currentIndex === index ? '' : String(ctrl.get('identification_id')?.value || ''))
      .filter(Boolean);

    const dialogRef = this.dialog.open(AgricolaBeneficiaryDialogComponent, {
      width: '560px',
      disableClose: true,
      data: {
        mode,
        beneficiary: current,
        availableParticipation: available,
        relationshipOptions: this.relationshipOptions,
        relationshipOptions$: this.relationshipOptionsSubject.asObservable(),
        relationshipLoading$: this.relationshipLoadingSubject.asObservable(),
        relationshipLoadedKey$: this.relationshipLoadedKeySubject.asObservable(),
        relationshipLoadFailed$: this.relationshipLoadFailedSubject.asObservable(),
        retryRelationship: () => this.retryLoadRelationships(),
        existingIdentificationIds,
        insuredIdentificationId: this.insuredIdentificationId
      }
    });

    dialogRef.afterClosed().subscribe((result: InsuranceBeneficiary | undefined) => {
      if (!result) {
        return;
      }

      if (mode === 'add') {
        this.beneficiaries.push(this.createBeneficiaryGroup(result));
        this.successMessage = 'Tu beneficiario se agregó correctamente. Verifica la información y continúa al siguiente paso.';
      } else if (mode === 'edit' && index !== undefined) {
        this.beneficiaries.at(index).patchValue(result);
        this.successMessage = 'Tu beneficiario se actualizó correctamente.';
      }

      this.updateTotalParticipation();
      this.syncBeneficiaryState();
      this.showSuccessBanner = true;
      this.showErrorBanner = false;
      this.errorMessage = '';
    });
  }

  private createBeneficiaryGroup(b: InsuranceBeneficiary): FormGroup {
    return this.fb.group({
      name: [b.name],
      relationship: [b.relationship],
      percentage: [b.percentage],
      identification_id: [b.identification_id],
      identification_type: [b.identification_type],
      birthday: [b.birthday || '']
    });
  }

  private syncBeneficiaryState(): void {
    this.agricolaService.setCurrentInsuranceBeneficiary(this.beneficiaries.value as InsuranceBeneficiary[]);
  }

  private printSavedBeneficiariesJson(): void {
    const savedBeneficiaries = this.agricolaService.currentInsuranceInfo?.beneficiarytable || [];
    console.log('Beneficiarios guardados en JSON:', JSON.stringify(savedBeneficiaries, null, 2));
  }

  private validateBeforeContinue(): boolean {
    if (this.beneficiaries.length === 0) {
      this.errorMessage = '';
      this.syncBeneficiaryState();
      return true;
    }

    if (this.totalParticipation !== 100) {
      this.errorMessage = 'El total de participación de beneficiarios debe ser igual a 100%.';
      return false;
    }

    const normalizedIds = this.beneficiaries.controls.map(ctrl => this.normalizeId(ctrl.get('identification_id')?.value));
    const hasDuplicates = new Set(normalizedIds).size !== normalizedIds.length;
    if (hasDuplicates) {
      this.errorMessage = 'No puedes agregar beneficiarios con el mismo número de identificación.';
      return false;
    }

    if (normalizedIds.includes(this.normalizeId(this.insuredIdentificationId))) {
      this.errorMessage = 'El beneficiario no puede tener el mismo número de identificación del asegurado.';
      return false;
    }

    const hasMissingRequired = this.beneficiaries.controls.some(ctrl =>
      !String(ctrl.get('name')?.value || '').trim() ||
      !String(ctrl.get('relationship')?.value || '').trim() ||
      !(Number(ctrl.get('percentage')?.value) >= 1) ||
      !String(ctrl.get('identification_id')?.value || '').trim() ||
      ctrl.get('identification_type')?.value == null ||
      ctrl.get('identification_type')?.value === ''
    );

    if (hasMissingRequired) {
      this.errorMessage = 'Por favor, completa todos los campos obligatorios de cada beneficiario.';
      return false;
    }

    this.errorMessage = '';
    this.syncBeneficiaryState();
    return true;
  }

  private normalizeId(value: unknown): string {
    return String(value || '').trim().toUpperCase();
  }

  getRelationshipLabel(value: unknown): string {
    const code = String(value || '');
    return this.relationshipOptions.find(option => option.value === code)?.label || code;
  }

  onAddBeneficiary(): void {
    this.openDialog('add');
  }

  onEditBeneficiary(index: number): void {
    this.openDialog('edit', index);
  }

  onDeleteBeneficiary(index: number): void {
    this.beneficiaries.removeAt(index);
    this.updateTotalParticipation();
    this.syncBeneficiaryState();
    this.showSuccessBanner = false;
  }

  closeSuccessBanner(): void {
    this.showSuccessBanner = false;
  }

  hideToast(): void {
    this.showToast = false;
  }

  showToastMessage(message: string, type: AgricolaToastType = 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;
  }

  // llamado desde el botón "Continuar" del stepper (mantenido por compatibilidad)
  onContinue(stepper: any): void {
    if (!this.validateBeforeContinue()) {
      this.showErrorBanner = true;
      this.showSuccessBanner = false;
      return;
    }

    this.showErrorBanner = false;
    this.printSavedBeneficiariesJson();
    stepper.next();
  }

  // Nuevo método para manejar el click del botón Continuar
  onContinueClick(): void {
    if (!this.validateBeforeContinue()) {
      this.showErrorBanner = true;
      this.showSuccessBanner = false;
      return;
    }

    this.showErrorBanner = false;
    this.printSavedBeneficiariesJson();
    // Emitir el índice del siguiente step para avanzar
    const nextIndex = this.selectedIndex + 1;
    this.nextStep.emit(nextIndex);
  }

  goBack(): void {
    // Emitir evento para que el componente padre maneje la navegación hacia atrás
    this.previousStep.emit();
  }

  goToStep(index: number): void {
    // Emitir evento para que el componente padre maneje la navegación al step específico
    this.nextStep.emit(index);
  }
}
